import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';

import { Button } from '@aurum/ui';

import { ArQuickLook } from '../ar/ArQuickLook';
import { useCartStore } from '../cart/cart-store';
import type { ApiMenuItem } from '../../lib/api';

import { controlsForCategory, type CustomizerControl } from './customizer-presets';
import { useCustomizer } from './useCustomizer';

interface Props {
  item: ApiMenuItem | null;
  categorySlug: string;
  onClose: () => void;
}

export function CustomizerSheet({ item, categorySlug, onClose }: Props): JSX.Element | null {
  const open = item !== null;
  return (
    <AnimatePresence>
      {open ? <Sheet item={item} categorySlug={categorySlug} onClose={onClose} /> : null}
    </AnimatePresence>
  );
}

function Sheet({
  item,
  categorySlug,
  onClose,
}: {
  item: ApiMenuItem;
  categorySlug: string;
  onClose: () => void;
}): JSX.Element {
  const controls = useMemo<CustomizerControl[]>(
    () => controlsForCategory(categorySlug),
    [categorySlug],
  );
  const initialChoices = useMemo<Record<string, string>>(
    () => Object.fromEntries(controls.map((c) => [c.key, c.options[0]?.value ?? ''])),
    [controls],
  );

  const [choices, setChoices] = useState<Record<string, string>>(initialChoices);
  const { state, visualize, reset } = useCustomizer(item.id, item.imageUrl);
  const addToCart = useCartStore((s) => s.add);

  function setChoice(key: string, value: string): void {
    setChoices((prev) => ({ ...prev, [key]: value }));
    reset();
  }

  function handleAddToCart(): void {
    addToCart({
      menuItemId: item.id,
      nameAr: item.nameAr,
      nameEn: item.nameEn,
      unitPrice: item.basePrice,
      currency: item.currency,
      customizations: choices,
      previewImageUrl: state.result?.imageUrl ?? null,
    });
    onClose();
  }

  return (
    <>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40 bg-onyx-900/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <motion.aside
        key="sheet"
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        exit={{ x: '-100%' }}
        transition={{ type: 'spring', stiffness: 240, damping: 30 }}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col overflow-y-auto border-l border-aurum-500/20 bg-onyx-700 shadow-aurum-deep"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-baseline justify-between border-b border-aurum-500/15 px-6 py-5">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-aurum-300">
              {item.sku}
            </p>
            <h2 className="font-display text-2xl text-aurum-200">{item.nameAr}</h2>
            <p className="font-body text-xs text-cream-100/60">{item.nameEn}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-aurum-sm border border-aurum-500/30 px-3 py-1 text-xs text-cream-100/80 hover:bg-aurum-500/10"
          >
            إغلاق
          </button>
        </header>

        <div className="flex flex-col gap-6 px-6 py-6">
          <Preview state={state} fallback={item.imageUrl} videoUrl={item.videoUrl} />

          <section>
            <h3 className="mb-3 font-display text-lg text-aurum-300">تخصيص</h3>
            <div className="space-y-4">
              {controls.map((control) => (
                <div key={control.key}>
                  <p className="mb-2 font-mono text-xs uppercase tracking-widest text-onyx-100">
                    {control.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {control.options.map((opt) => {
                      const active = choices[control.key] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setChoice(control.key, opt.value)}
                          className={
                            'rounded-aurum-sm border px-3 py-1.5 text-sm transition ' +
                            (active
                              ? 'border-aurum-500 bg-aurum-500/15 text-aurum-200'
                              : 'border-aurum-500/20 text-cream-100/70 hover:border-aurum-500/40')
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <ArQuickLook menuItemId={item.id} itemNameAr={item.nameAr} />

          <div className="flex flex-col gap-2">
            <Button
              variant="secondary"
              isLoading={state.isLoading}
              onClick={() => visualize(choices)}
            >
              {state.isLoading ? 'جاري التحضير...' : 'عرض الصورة'}
            </Button>
            <Button size="lg" onClick={handleAddToCart}>
              إضافة إلى الطلب
            </Button>
          </div>

          {state.error ? (
            <p className="rounded-aurum-sm border border-accent-ruby/40 bg-accent-ruby/10 px-3 py-2 text-sm text-accent-ruby">
              {state.error}
            </p>
          ) : null}

          {state.result?.fromCache ? (
            <p className="font-mono text-xs text-aurum-300/80">
              ⚡ تم العرض من الذاكرة المؤقتة (cache hit)
            </p>
          ) : null}
        </div>
      </motion.aside>
    </>
  );
}

function Preview({
  state,
  fallback,
  videoUrl,
}: {
  state: ReturnType<typeof useCustomizer>['state'];
  fallback: string | null;
  videoUrl: string | null;
}): JSX.Element {
  if (state.isLoading) {
    return (
      <div
        className="aurum-shimmer aspect-square w-full rounded-aurum-lg border border-aurum-500/15 bg-onyx-900/40"
        aria-label="جاري توليد الصورة"
      />
    );
  }
  if (videoUrl && !state.result) {
    return (
      <motion.video
        key={videoUrl}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        src={videoUrl}
        autoPlay
        muted
        loop
        playsInline
        className="aspect-square w-full rounded-aurum-lg border border-aurum-500/15 object-cover"
      />
    );
  }
  const url = state.result?.imageUrl ?? fallback;
  if (!url) {
    return (
      <div className="grid aspect-square w-full place-items-center rounded-aurum-lg border border-aurum-500/15 bg-onyx-900/40 text-sm text-cream-100/40">
        لا توجد صورة معاينة بعد
      </div>
    );
  }
  return (
    <motion.img
      key={url}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      src={url}
      alt=""
      className="aspect-square w-full rounded-aurum-lg border border-aurum-500/15 object-cover"
    />
  );
}
